import { Link } from 'react-router-dom'
import { useDocumentMeta } from '../hooks/useDocumentMeta'

export default function About() {
  useDocumentMeta({
    title: 'Psykoterapeut Svendborg | Om Birgitte Vilsgaard - Terapien.dk',
    description: 'Mød Birgitte Vilsgaard – uddannet pædagog og psykoterapeut i Svendborg. Erfaring med individuel psykoterapi, parterapi og familieterapi på Fyn.',
    image: 'https://assets.auxodev.dk/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/shutterstock-1756005818.jpg'
  })
  return (
    <div className="pt-[72px] min-h-screen bg-brand-offwhite">
      {/* Hero Section */}
      <section className="relative bg-brand-dark text-brand-offwhite py-20 lg:py-28 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/shutterstock-1756005818.jpg"
            alt="Birgitte Vilsgaard - Om mig"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/85 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 relative z-10 flex flex-col gap-4">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-sage">
            <i className="fa-solid fa-user text-[10px]" /> Mød din terapeut
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-sand leading-tight">
            Om mig
          </h1>
          <p className="text-base sm:text-lg font-serif italic text-brand-offwhite/85 max-w-3xl leading-relaxed">
            <span className="block not-italic font-semibold text-brand-sand/90 text-lg sm:text-xl mb-1">
              Birgitte Vilsgaard
            </span>
            Reg. psykoterapeut i Svendborg · Familiebehandler · Terapi på Fyn
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left Column: Portrait & Quick Stats */}
          <div className="lg:col-span-4 flex flex-col gap-8 lg:sticky lg:top-28 self-start">
            <div className="relative">
              <div className="absolute -inset-3 border border-brand-sage/20 rounded-2xl rotate-2" />
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/shutterstock-1756005818.jpg"
                alt="Birgitte Vilsgaard - Integrativ psykoterapeut"
                className="relative rounded-2xl shadow-xl border-4 border-brand-offwhite w-full h-auto object-cover"
              />
              {/* Asset URL reference */}
              <img
                src="https://assets.auxodev.dk/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/logo.png"
                alt="BG-Psykoterapeut reference"
                className="hidden"
                aria-hidden="true"
              />
            </div>

            {/* Professional Memberships & Core Roles */}
            <div className="bg-brand-sand/10 border border-brand-sand/30 rounded-2xl p-6 flex flex-col gap-5">
              <h2 className="font-serif text-lg font-bold text-brand-dark">Faglige Arbejdsområder</h2>
              <div className="space-y-4 text-sm text-brand-dark">
                <div>
                  <span className="block text-xs uppercase tracking-wider text-brand-sage font-bold mb-1.5">Terapi & Supervision</span>
                  <ul className="space-y-1.5">
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-sage shrink-0" />
                      <span className="font-medium">Integrativ psykoterapeut</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-sage shrink-0" />
                      <span className="font-medium">Supervisor</span>
                    </li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-brand-sand/30">
                  <span className="block text-xs uppercase tracking-wider text-brand-sage font-bold mb-1.5">Myndighedsopgaver</span>
                  <ul className="space-y-1.5">
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-sage shrink-0" />
                      <span className="font-medium">Familiebehandling</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-sage shrink-0" />
                      <span className="font-medium">Samværskonsulent</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-sage shrink-0" />
                      <span className="font-medium">Støttekontaktperson</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-sage shrink-0" />
                      <span className="font-medium">Komplekse familieindsatser</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="border-t border-brand-sand/30 pt-4 flex flex-col gap-3">
                <div className="flex justify-center mt-1">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=80,format=auto/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/fadp-logo-reg-psykoterapeut-300x150.jpg"
                    alt="FaDP Logo"
                    className="h-14 w-auto object-contain"
                  />
                </div>
                <a
                  href="https://fadp.dk/fadps-etiske-regler/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold uppercase tracking-wider text-brand-dark hover:text-brand-sage transition-colors duration-150 text-center flex items-center justify-center gap-1.5"
                >
                  FaDP’s etiske regler <i className="fa-light fa-arrow-up-right-from-square text-[10px]" />
                </a>
              </div>
            </div>
          </div>

          {/* Right Column: Detailed Story */}
          <div className="lg:col-span-8 flex flex-col gap-12 text-brand-gray/90 leading-relaxed">
            
            {/* SECTION 1: Filosofi & Terapiproces */}
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-4">
                <span className="text-xs uppercase tracking-[0.2em] text-brand-sage font-semibold">Filosofi & Tilgang</span>
                <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark leading-tight">
                  Du fortjener at have det godt. Jeg er her for at hjælpe.
                </h2>
                <div className="h-1 w-12 bg-brand-sage rounded-full" />
              </div>

              {/* Highlighted Quote Box */}
              <div className="relative bg-brand-sand/20 border-l-4 border-brand-sage rounded-r-3xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.01)]">
                <i className="fa-solid fa-quote-left text-brand-sage/20 text-4xl absolute top-4 right-6 pointer-events-none" />
                <p className="font-serif italic text-lg sm:text-xl text-brand-dark/90 relative z-10 leading-relaxed">
                  "Du behøver ikke være stærk hele tiden. Måske fylder tankerne for meget, måske står du i sorg eller svære relationer. <span className="text-brand-dark font-medium underline decoration-brand-sage/50 underline-offset-4">Hos mig er der plads til dig – også når livet gør ondt.</span>"
                </p>
                <span className="block text-xs uppercase tracking-wider text-brand-sage font-semibold mt-3 relative z-10">
                  Birgitte Vilsgaard · Terapien.dk
                </span>
              </div>

              <p className="text-sm sm:text-base text-brand-gray/80 leading-relaxed">
                Som psykoterapeut i Svendborg med praksis på Sydfyn møder jeg dig i øjenhøjde. Uanset om du henvender dig med ønske om individuel psykoterapi i Svendborg, parterapi eller familieterapi på Fyn, er målet at skabe tryghed, genfinde retning og give dig nye handlemuligheder i hverdagen.
              </p>

              {/* The Therapy Journey Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                <div className="bg-brand-offwhite border border-brand-sand/40 rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.01)] flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-sand/30 flex items-center justify-center text-brand-sage">
                    <i className="fa-solid fa-door-open text-sm" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="font-serif font-bold text-brand-dark">Det første skridt</h3>
                    <p className="text-sm text-brand-gray/80 leading-relaxed">
                      Det kræver faktisk et stort mod og overvindelse, at skulle starte op i terapi! Og det er ikke uden grund, at der kan være modstand på, samt mange overspringshandlinger for at udskyde hjælpen.
                    </p>
                  </div>
                </div>

                <div className="bg-brand-offwhite border border-brand-sand/40 rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.01)] flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-sand/30 flex items-center justify-center text-brand-sage">
                    <i className="fa-solid fa-compass text-sm" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="font-serif font-bold text-brand-dark">En dybdegående rejse</h3>
                    <p className="text-sm text-brand-gray/80 leading-relaxed">
                      En terapiproces kan være en hård og lang rejse. Det is ikke altid “lutter lagkage” at skulle arbejde i de dybere lag. Det kan være drøjt at skulle arbejde med, og forholde sig til sin egen historie og billede af sig selv. Men det hårde arbejde giver som regel afkast.
                    </p>
                  </div>
                </div>

                <div className="bg-brand-offwhite border border-brand-sand/40 rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.01)] flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-sand/30 flex items-center justify-center text-brand-sage">
                    <i className="fa-solid fa-feather text-sm" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="font-serif font-bold text-brand-dark">Accept & Frigørelse</h3>
                    <p className="text-sm text-brand-gray/80 leading-relaxed">
                      Undervejs i arbejdet vil det også opleves befriende, at give sig selv den nødvendige plads – og lære at acceptere sig selv på godt og ondt, samt at være med det. -For det er ikke anderledes end, “at jeg er som jeg er”!
                    </p>
                  </div>
                </div>

                <div className="bg-brand-offwhite border border-brand-sand/40 rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.01)] flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-sand/30 flex items-center justify-center text-brand-sage">
                    <i className="fa-solid fa-seedling text-sm" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="font-serif font-bold text-brand-dark">Nye perspektiver</h3>
                    <p className="text-sm text-brand-gray/80 leading-relaxed">
                      Når rejsen afsluttes eller sættes på et midlertidigt stop, så vil du højest sandsynligt føle dig lettet og befriet i kroppen, og få et godt overskud til hverdagen. -Du vil sandsynligvis se dig selv med nogle helt nye øjne.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottom Philosophy Callout */}
              <div className="bg-brand-sage/10 border border-brand-sage/20 rounded-3xl p-6 text-center flex flex-col gap-2">
                <p className="font-serif font-bold text-lg text-brand-dark">
                  -Og det fortjener du!
                </p>
                <p className="text-sm text-brand-gray/80">
                  Og det mest fantastiske ved terapiprocessen er, at den aldrig behøver at stoppe.
                </p>
              </div>

              {/* SECTION 2: Myndighedsopgaver */}
              <div className="mt-8 pt-8 border-t border-brand-sand/40 flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <span className="text-xs uppercase tracking-[0.2em] text-brand-sage font-semibold">Kommunale opgaver & forvaltninger</span>
                  <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark">
                    Kompetencer indenfor Myndighedsopgaver
                  </h2>
                  <div className="h-1 w-12 bg-brand-sage rounded-full mt-1" />
                </div>
                <p className="text-sm sm:text-base text-brand-gray/80 leading-relaxed">
                  Jeg udfører specialiserede opgaver for kommunale forvaltninger, familieafdelinger og anbringelsessteder. Med udgangspunkt i høj faglig etik, skriftlig dokumentation og barnets tarv tilbyder jeg løsninger inden for:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Familiebehandling */}
                  <div className="bg-brand-sand/15 border border-brand-sand/30 rounded-3xl p-6 flex flex-col gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-sage/20 text-brand-dark flex items-center justify-center text-sm font-semibold">
                      <i className="fa-solid fa-people-roof" />
                    </div>
                    <h3 className="font-serif text-lg font-bold text-brand-dark">Familiebehandling</h3>
                    <p className="text-xs sm:text-sm text-brand-gray/80 leading-relaxed">
                      Målrettet og helhedsorienteret støtte til familier med komplekse relationelle udfordringer. Vi arbejder med forældrekompetencer, tilknytning, grænsesætning og trivsel for alle familiens medlemmer.
                    </p>
                  </div>

                  {/* Samværskonsulent */}
                  <div className="bg-brand-sand/15 border border-brand-sand/30 rounded-3xl p-6 flex flex-col gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-sage/20 text-brand-dark flex items-center justify-center text-sm font-semibold">
                      <i className="fa-solid fa-hands-holding-child" />
                    </div>
                    <h3 className="font-serif text-lg font-bold text-brand-dark">Samværskonsulent</h3>
                    <p className="text-xs sm:text-sm text-brand-gray/80 leading-relaxed">
                      Støttet og overvåget samvær mellem anbragte børn og deres biologiske forældre. Sikrer trygge rammer for barnet samt grundig, neutral og faglig observation og rapportering til sagsbehandler.
                    </p>
                  </div>

                  {/* Støttekontaktperson */}
                  <div className="bg-brand-sand/15 border border-brand-sand/30 rounded-3xl p-6 flex flex-col gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-sage/20 text-brand-dark flex items-center justify-center text-sm font-semibold">
                      <i className="fa-solid fa-person-walking" />
                    </div>
                    <h3 className="font-serif text-lg font-bold text-brand-dark">Støttekontaktperson</h3>
                    <p className="text-xs sm:text-sm text-brand-gray/80 leading-relaxed">
                      Tæt og tillidsskabende støtte til sårbare børn, unge eller voksne, der har brug for en stabil voksenkontakt, hjælp til hverdagsstruktur, skolegang eller personlig mestring.
                    </p>
                  </div>

                  {/* Komplekse familieindsatser */}
                  <div className="bg-brand-sand/15 border border-brand-sand/30 rounded-3xl p-6 flex flex-col gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-sage/20 text-brand-dark flex items-center justify-center text-sm font-semibold">
                      <i className="fa-solid fa-shield-heart" />
                    </div>
                    <h3 className="font-serif text-lg font-bold text-brand-dark">Komplekse familieindsatser</h3>
                    <p className="text-xs sm:text-sm text-brand-gray/80 leading-relaxed">
                      Tværfaglige og intensive forløb i familier med svære problemstillinger som traumer, misbrug, skilsmissekonflikter, psykiatriske diagnoser eller akutte kriser.
                    </p>
                  </div>
                </div>

                <div className="bg-brand-sand/20 border border-brand-sand/40 rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-file-invoice text-brand-sage text-2xl" />
                    <span className="text-sm font-medium text-brand-dark">
                      Timepris for myndighedsopgaver: <strong>750 kr.</strong> (+ tillæg ved aften, weekender & helligdage)
                    </span>
                  </div>
                  <Link
                    to="/priser"
                    className="text-xs font-semibold uppercase tracking-wider text-brand-dark hover:text-brand-sage underline flex items-center gap-1"
                  >
                    Se priser <i className="fa-solid fa-arrow-right text-[10px]" />
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-sand/15 border-t border-brand-sand/20">
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col items-center gap-6">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark">
            Klar til at tage det første skridt?
          </h2>
          <p className="text-brand-gray/80 max-w-xl text-base">
            Du er altid velkommen til at kontakte mig for en uforpligtende samtale om, hvordan jeg kan hjælpe dig.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 w-full sm:w-auto pt-2">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
            >
              Book en samtale
            </Link>
            <Link
              to="/priser"
              className="w-full sm:w-auto bg-brand-offwhite border border-brand-dark/30 hover:border-brand-dark text-brand-dark text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-300 hover:bg-white hover:scale-[1.02] active:scale-[0.98]"
            >
              Se priser
            </Link>
            <Link
              to="/kontakt"
              className="w-full sm:w-auto bg-brand-sand/30 border border-brand-sand/70 hover:border-brand-dark text-brand-dark text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-300 hover:bg-white hover:scale-[1.02] active:scale-[0.98]"
            >
              Kontakt mig
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
